import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import RoleplayEditor from "@nualang/nualang-ui-components/dist/Editors/Roleplay";
import {
  courses as courseQuerys,
  roleplays as roleplayQuerys,
  avatars as avatarQuerys,
  users as userQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import useServices from "../../../hooks/useServices";
import useAudioPlayer from "../../../hooks/useAudioPlayer";
import config from "../../../config";
import { UploadRoleplayImage } from "../../../utils/imageUpload";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import {
  highlightSpeechText,
  stopHighlightingSpeechText,
} from "../../../utils/index";
import {
  updateRoleplay as updateRoleplayAPI,
  getRoleplay,
} from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import {
  avatars as getAvatars,
  makeChatGptApiRequest,
} from "@nualang/nualang-api-and-queries/APIs/Services";
import { getAiQuery as makeCandooAIQuery } from "../../../utils/api/CandooAI/apis/AiQuery";
import { getUser } from "@nualang/nualang-api-and-queries/APIs/Users";
import {
  roleplays as roleplayHelpers,
  courses as courseHelpers,
} from "../../../utils/helpers";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "../../../i18n";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { difficulties, getForLangCharacters } from "../../../utils/index";
import { useApp, useSubscription } from "../../../hooks";
import logger from "../../../logger";

function EditRoleplay(props) {
  const { t } = useTranslation();
  const [confirm] = useConfirm(t);
  const [tts, translate] = useServices();

  const {
    authData: { username, attributes },
    navigate,
    verificationStatus,
  } = props;

  const { addToQueue, stop, clearQueue } = useAudioPlayer();
  const params = useParams();
  const { classroomId, courseId, sectionId, topicId, roleplayId } = params;

  const queryClient = useQueryClient();

  const {
    openSnackbar,
    handleShareRoleplay,
    handleOpenModerateDialog,
  } = useApp();
  const { subscription } = useSubscription();

  const [isRoleplayEditorOpen, setIsRoleplayEditorOpen] = useState(true);
  const [timeoutArray, setTimeoutArray] = useState([]);

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { forLang = "" } = course;

  const handleCloseRoleplayEditor = () => {
    setIsRoleplayEditorOpen(false);
    if (classroomId && courseId && sectionId && topicId) {
      navigate(
        `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
      );
    } else if (courseId && sectionId && topicId) {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
    } else {
      navigate("/roleplays");
    }
  };

  const userQuery = userQuerys.useUser(
    async (userId) => {
      const response = await getUser(userId, false);
      return response;
    },
    {
      userId: username,
    },
    {
      enabled: !!username,
    },
  );

  const currentUser =
    userQuery.isSuccess && userQuery.data ? userQuery.data : {};

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const avatarsQuery = avatarQuerys.useAvatars(async () => {
    const avatars = await getAvatars();
    return avatars;
  });

  const avatars =
    avatarsQuery.isSuccess &&
      Array.isArray(avatarsQuery.data) &&
      avatarsQuery.data.length
      ? avatarsQuery.data
      : [];

  const roleplayQuery = roleplayQuerys.useRoleplay(
    async (rId) => {
      const roleplay = await getRoleplay(rId);
      const formattedRoleplay = await roleplayHelpers.formatRoleplay(
        roleplay.Item,
        username,
      );
      return formattedRoleplay;
    },
    {
      roleplayId,
    },
    { enabled: !!roleplayId },
  );

  const roleplay =
    roleplayQuery.isSuccess &&
      roleplayQuery.data &&
      roleplayQuery.data.roleplayId
      ? roleplayQuery.data
      : {};

  const { learnLang } = roleplay;

  const topicQuery = courseQuerys.useTopic(
    async (cId, sId, tId) => {
      const topic = await getCourseSectionTopic(cId, sId, tId);
      const formattedTopic = await courseHelpers.formatTopic(
        topic.Item,
        props.authData,
      );
      return formattedTopic;
    },
    {
      courseId,
      sectionId,
      topicId,
      username: props.authData.username,
    },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const courseSectionTopic =
    topicQuery.isSuccess && topicQuery.data && topicQuery.data.topicId
      ? topicQuery.data
      : {};

  const phraseListsQuery = courseQuerys.useCourseSectionTopicPhraseLists(
    undefined,
    { courseId, sectionId, topicId, filters: { includePhrases: "true" } },
    { enabled: !!courseId && !!sectionId && !!topicId },
  );

  const phraseLists =
    phraseListsQuery.isSuccess &&
      phraseListsQuery.data &&
      Array.isArray(phraseListsQuery.data.Items)
      ? phraseListsQuery.data.Items.map((pl) => ({
        id: pl.phraseListId,
        name: pl.phraseListName,
        phrases: Array.isArray(pl.phrases)
          ? [...pl.phrases].sort((a, b) => (a?.idx ?? 0) - (b?.idx ?? 0))
          : [],
      }))
      : [];

  const allTopicPhrases =
    Array.isArray(phraseLists) && phraseLists.length > 0
      ? phraseLists.flatMap((pl) => pl.phrases)
      : [];

  // Mutations
  const updateRoleplayMutation = roleplayQuerys.useUpdateRoleplay(
    updateRoleplayAPI,
    {
      onSuccess: (data, variables) => {
        roleplayHelpers.invalidateRoleplayCache({
          roleplayId: variables.roleplayId,
          ...variables.roleplay,
          ...variables.values,
          ...data.Attributes,
        });
        queryClient.invalidateQueries({
          queryKey: roleplayQuerys.roleplayKeys.list({
            createdBy: username,
          }),
        });
        if (courseId && sectionId && topicId) {
          // invalidate for view topic page
          queryClient.invalidateQueries({
            queryKey: roleplayQuerys.roleplayKeys.list({
              courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
              limit: config.limits.roleplays,
            }),
          });
        }
        openSnackbar("updated", "success");
      },
      onError: () => {
        openSnackbar("problem", "error");
      },
    },
  );

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        isHoverText = false,
        pitch,
      } = params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        null,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback, isHoverText);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  const handleUpdateRoleplay = async (values, bag) => {
    try {
      if (values.visibility) {
        if (
          values.visibility &&
          values.visibility !== "public" &&
          values.visibility !== "private"
        ) {
          throw new Error();
        }
      }
      if (values.difficulty) {
        if (!difficulties.includes(values.difficulty)) {
          throw new Error();
        }
      }
      if (values.roleplayId) {
        delete values.roleplayId;
      }
      if (values.actor1 && values.actor1.picture) {
        values.actor1PictureWarnDetails = {};
        const base64String = values.actor1.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplayId}-actor1`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.actor1PictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.actor1.picture = picture;
          values.actor1.pictureS3Key = pictureS3Key;
        } else {
          // instead of deleting the picture assign it the old picture value
          values.actor1.picture = roleplay.actor1.picture;
          values.actor1.pictureS3Key = roleplay.actor1.pictureS3Key;
        }
      }
      if (values.actor2 && values.actor2.picture) {
        values.actor2PictureWarnDetails = {};
        const base64String = values.actor2.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplayId}-actor2`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.actor2PictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.actor2.picture = picture;
          values.actor2.pictureS3Key = pictureS3Key;
        } else {
          // instead of deleting the picture assign it the old picture value
          values.actor2.picture = roleplay.actor2.picture;
          values.actor2.pictureS3Key = roleplay.actor2.pictureS3Key;
        }
      }
      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadRoleplayImage(
            base64String[1] || base64String[0],
            `${roleplayId}`,
          );
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            if (confirmation !== true) {
              return;
            }
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }

      updateRoleplayMutation.mutate({
        roleplayId: roleplay.roleplayId,
        values,
        roleplay,
      });
      if (bag) {
        if (bag.resetForm) {
          bag.resetForm({ values });
        }
        bag.setSubmitting(false);
      }
      return true;
    } catch (error) {
      logger.error(error);
      if (bag) {
        if (bag.resetForm) {
          bag.resetForm({ values });
        }
        bag.setSubmitting(false);
      }
      return false;
    }
  };

  const handlePlayRoleplay = async (roleplay) => {
    const { courseSectionTopicId, roleplayId } = roleplay;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (classroomId && courseId && sectionId && topicId) {
      navigate(
        `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}`,
      );
    } else if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      navigate(
        `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}`,
      );
    } else {
      navigate(`/roleplays/${roleplayId}`);
    }
  };

  const handleEditRoleplay = async (roleplay) => {
    const { courseSectionTopicId, roleplayId } = roleplay;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      navigate(
        `/courses/${courseId}/${sectionId}/${topicId}/roleplays/${roleplayId}/edit`,
      );
    } else {
      navigate(`/roleplays/${roleplayId}/edit`);
    }
  };

  const siteLanguage = (() => {
    const languageMap = {
      en: "English",
      "en-US": "English",
      it: "Italian",
      de: "German",
      ga: "Irish",
      pt: "Portuguese",
      fr: "French",
      es: "Spanish",
      uk: "Ukrainian",
    };

    return languageMap[i18n.language] ?? "your browser language";
  })();

  const getAllVoices = () => {
    let voicesArray = [];
    if (config) {
      const voices = config.languages[learnLang || "english"].Voices;
      const keys = Object.keys(voices);
      keys.forEach((key) => {
        voicesArray.push(...voices[key].voiceOptions);
      });
    }
    return voicesArray;
  };

  const learnLangCharacters = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].characters
    : [];
  const forLangCharacters = getForLangCharacters(forLang);
  let specialCharacters = learnLangCharacters.concat(forLangCharacters);
  specialCharacters = [...new Set(specialCharacters)];

  return (
    <div>
      {roleplay && roleplay.roleplayId && (
        <RoleplayEditor
          t={t}
          initialValues={roleplay}
          handleClose={handleCloseRoleplayEditor}
          open={isRoleplayEditorOpen}
          onSubmit={handleUpdateRoleplay}
          learnLangCharacters={specialCharacters}
          forLangCharacters={specialCharacters}
          avatars={avatars}
          voices={
            learnLang
              ? config.languages[learnLang.toLowerCase() || "english"].Voices
              : []
          }
          isRoleplayHidden={roleplay ? roleplay.isRoleplayHidden : null}
          courseId={courseId}
          sectionId={sectionId}
          topicId={topicId}
          navigate={navigate}
          handleSpeak={handleSpeak}
          handleStopSpeaking={handleStopSpeaking}
          learnLang={learnLang}
          forLang={forLang}
          openSnackbar={openSnackbar}
          exerciseName="roleplay"
          handleTranslate={translate}
          userAttributes={attributes}
          handlePlayRoleplay={handlePlayRoleplay}
          handleEditRoleplay={handleEditRoleplay}
          handleShareRoleplay={handleShareRoleplay}
          fileSizeLimit={config.fileSizeLimit}
          subscription={subscription}
          makeCandooAIQuery={makeCandooAIQuery}
          makeChatGptApiRequest={makeChatGptApiRequest}
          currentUser={currentUser}
          pdfDetails={course && course.pdfDetails ? course.pdfDetails : ""}
          pdfVectorStoreCreationStatus={
            course && course.pdfVectorStoreCreationStatus
              ? course.pdfVectorStoreCreationStatus
              : ""
          }
          getAllVoices={getAllVoices}
          i18nLanguage={i18n.language}
          siteLanguage={siteLanguage}
          phrases={
            allTopicPhrases.length > 0
              ? allTopicPhrases
              : (courseSectionTopic.phrases || [])
          }
          phraseLists={phraseLists}
          initialTopicGoal={
            courseSectionTopic && courseSectionTopic.topicGoal
              ? courseSectionTopic.topicGoal
              : ""
          }
          isUserInternal={isUserInternal}
          verificationStatus={verificationStatus}
        />
      )}
    </div>
  );
}

EditRoleplay.propTypes = {
  navigate: PropTypes.func,
  authData: PropTypes.object,
};

export default EditRoleplay;
